import { useEffect, useState, useCallback } from "react";
import { Users, CalendarClock, CalendarDays, Timer, RefreshCw, LogOut, GraduationCap, Clock, Siren, Loader2, History, Download } from "lucide-react";
import api, { formatApiErrorDetail, API_BASE, BROWSER_TZ } from "@/lib/api";
import { fmtHM } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { displayName } from "@/lib/format";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    currently_inside: 0,
    todays_checkins: 0,
    week_checkins: 0,
    avg_duration_minutes: 0,
    students_on_premises: 0,
    todays_student_events: 0,
  });
  const [active, setActive] = useState([]);
  const [students, setStudents] = useState([]);
  const [companyType, setCompanyType] = useState("business");
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [evacBusy, setEvacBusy] = useState(false);

  const evacuationPdf = async () => {
    setEvacBusy(true);
    try {
      const token = localStorage.getItem("vlog_token");
      const res = await fetch(`${API_BASE}/evacuation/roll-call.pdf`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "X-Timezone": BROWSER_TZ },
      });
      if (!res.ok) throw new Error("Roll-call failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `logerd-evacuation-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Evacuation roll-call downloaded — print and take it to the assembly point.");
    } catch (e) {
      toast.error(e.message || "Roll-call failed");
    } finally {
      setEvacBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, c] = await Promise.all([
        api.get("/visits/stats"),
        api.get("/visits", { params: { status: "inside", limit: 200 } }),
        api.get("/me/company"),
      ]);
      setStats(s.data);
      setActive(a.data);
      setCompanyType(["school", "daycare"].includes(c.data.type) ? "school" : (c.data.type || "business"));
      if (["school", "daycare"].includes(c.data.type || "business")) {
        const sp = await api.get("/students/on-premises");
        setStudents(sp.data);
      } else {
        setStudents([]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const forceCheckout = async (id, name) => {
    setPendingId(id);
    try {
      await api.patch(`/visits/${id}/force-checkout`);
      toast.success(`${name} checked out`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Overview</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DrillHistoryDialog />
          <button
            onClick={evacuationPdf}
            disabled={evacBusy}
            data-testid="evacuation-rollcall-btn"
            title="Print a roll-call of everyone currently inside"
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {evacBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Siren className="h-4 w-4" />} Evacuation
          </button>
          <button
            onClick={load}
            data-testid="dashboard-refresh-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="stats-cards">
        {companyType === "school" ? (
          <>
            <StatCard Icon={GraduationCap} label="Students on premises" value={stats.students_on_premises} accent />
            <StatCard Icon={Users} label="Visitors inside" value={stats.currently_inside} />
            <StatCard Icon={CalendarClock} label="Today's student events" value={stats.todays_student_events} />
            <StatCard Icon={CalendarDays} label="Visitors this week" value={stats.week_checkins} />
          </>
        ) : (
          <>
            <StatCard Icon={Users} label="Currently Inside" value={stats.currently_inside} accent />
            <StatCard Icon={CalendarClock} label="Today's Check-ins" value={stats.todays_checkins} />
            <StatCard Icon={CalendarDays} label="This Week" value={stats.week_checkins} />
            <StatCard Icon={Timer} label="Avg Duration" value={fmtHM(stats.avg_duration_minutes)} />
          </>
        )}
      </section>

      {companyType === "school" && (
        <section className="rounded-xl border border-slate-200 bg-white mb-6" data-testid="students-onprem-section">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <div>
              <div className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><GraduationCap className="h-5 w-5 text-primary" /> Students on premises</div>
              <div className="text-xs text-slate-500">Dropped off today and not yet picked up.</div>
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-500">{students.length} student{students.length === 1 ? "" : "s"}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="students-onprem-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-5 py-3">Student</th>
                  <th className="px-5 py-3">Grade</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Officer</th>
                  <th className="px-5 py-3">Since</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 && !loading && (
                  <tr><td colSpan={5} className="px-5 py-14 text-center text-slate-500">No students currently on premises.</td></tr>
                )}
                {students.map((s) => (
                  <tr key={s.student_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`student-onprem-${s.student_id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">{s.student_name}</td>
                    <td className="px-5 py-3 text-slate-700">{s.student_grade || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                        s.latest_event === "tardy" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
                      }`}>
                        <Clock className="h-3 w-3" /> {s.latest_event === "tardy" ? "Tardy" : "Dropped off"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{displayName(s.officer_name) || "—"}</td>
                    <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{new Date(s.latest_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="font-display text-lg font-semibold text-slate-900">{companyType === "school" ? "Visitors currently in school" : "Currently Inside"}</div>
            <div className="text-xs text-slate-500">{companyType === "school" ? "Non-parent visitors signed in at the kiosk." : "Live list of people in the building"}</div>
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{active.length} {companyType === "school" ? "visitor" : "people"}{active.length === 1 ? "" : "s"}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="active-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Host</th>
                <th className="px-5 py-3">Purpose</th>
                <th className="px-5 py-3">Officer</th>
                <th className="px-5 py-3">Checked In</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {active.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center text-slate-500">
                    No one is currently inside.
                  </td>
                </tr>
              )}
              {active.map((v) => (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`active-row-${v.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{v.full_name}</div>
                    <div className="text-xs text-slate-500">{v.phone}</div>
                  </td>
                  <td className="px-5 py-3"><TypeBadge type={v.visitor_type} /></td>
                  <td className="px-5 py-3 text-slate-700">{v.company || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{v.host_name}</td>
                  <td className="px-5 py-3 text-slate-700 max-w-xs truncate">{v.purpose}</td>
                  <td className="px-5 py-3 text-slate-700">{displayName(v.officer_name) || <span className="text-slate-400">—</span>}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">
                    {new Date(v.checked_in_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => forceCheckout(v.id, v.full_name)}
                      disabled={pendingId === v.id}
                      data-testid={`force-checkout-${v.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 transition"
                    >
                      <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatCard({ Icon, label, value, accent }) {
  return (
    <div className={`rounded-xl border bg-white p-5 ${accent ? "border-primary/20 ring-1 ring-primary/10" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-slate-400"}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}

export function TypeBadge({ type }) {
  if (type === "contractor")
    return <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-blue-800">Contractor</span>;
  return <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-700">Visitor</span>;
}

function DrillHistoryDialog() {
  const [open, setOpen] = useState(false);
  const [drills, setDrills] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    setDrills(null);
    try {
      const { data } = await api.get("/evacuation/drills");
      setDrills(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load drill history");
      setDrills([]);
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("vlog_token");
      const res = await fetch(`${API_BASE}/evacuation/drills/export.csv`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "X-Timezone": BROWSER_TZ },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `logerd-drill-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Drill log downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <>
      <button
        onClick={openDialog}
        data-testid="drill-history-btn"
        title="View every evacuation roll-call run"
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
      >
        <History className="h-4 w-4" /> Drill history
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="drill-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Siren className="h-5 w-5 text-rose-600" /> Evacuation drill history
            </DialogTitle>
            <DialogDescription>
              Every roll-call run is recorded here — export the log to prove drill compliance to inspectors.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <button
              onClick={exportCsv}
              disabled={!drills || drills.length === 0}
              data-testid="drill-export-csv"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-white hover:shadow disabled:opacity-50 transition"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-slate-200">
            {drills === null && (
              <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
            )}
            {drills !== null && drills.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="drill-history-empty">
                No drills recorded yet — tap the red Evacuation button to run your first roll-call.
              </div>
            )}
            {drills !== null && drills.length > 0 && (
              <table className="w-full text-sm" data-testid="drill-history-table">
                <thead className="sticky top-0">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Run by</th>
                    <th className="px-4 py-2.5 text-right">Visitors</th>
                    <th className="px-4 py-2.5 text-right">Students</th>
                    <th className="px-4 py-2.5 text-right">Staff</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {drills.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100" data-testid={`drill-row-${d.id}`}>
                      <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{new Date(d.run_at).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-slate-700">{d.run_by || "—"}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700">{d.visitors}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700">{d.students}</td>
                      <td className="px-4 py-2.5 text-right text-slate-700">{d.staff}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{d.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
